import DataTable from "../components/common/DataTable";
import HeaderInfo from "../components/common/HeaderInfo";
import { ProfileIcon, Search } from "../components/icons";
import itmImg from "../assets/item-thumbnail.png";
import { useEffect, useState } from "react";
import {
  useGetCategoriesQuery,
  useGetCampusStockAllotmentsQuery,
  useLazyGetCampusStockAllotmentsQuery,
} from "../store/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Download } from "lucide-react";
import toast from "react-hot-toast";

function formatAllotmentDate(dateString) {
  if (!dateString) return { date: "N/A", time: "N/A" };

  const date = new Date(dateString);

  return {
    date: date.toLocaleDateString("en-GB"),
    time: date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }),
  };
}

export default function CampusAllotments() {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedCategory, fromDate, toDate, limit]);

  const { data, isLoading, isFetching } = useGetCampusStockAllotmentsQuery({
    page,
    limit: limit === "All" ? undefined : limit,
    showAll: limit === "All" ? true : undefined,
    ...(debouncedSearch && { search: debouncedSearch }),
    category: selectedCategory,
    ...(fromDate && { fromDate }),
    ...(toDate && { toDate }),
  });

  const [triggerLazyGetCampusStockAllotments, { isFetching: isGenerating }] = useLazyGetCampusStockAllotmentsQuery();

  const handleGenerateReport = async () => {
    if (allotments.length === 0) {
      toast.error("No data available to generate report");
      return;
    }
    try {
      const response = await triggerLazyGetCampusStockAllotments({
        search: debouncedSearch,
        category: selectedCategory,
        fromDate,
        toDate,
        showAll: true,
      }).unwrap();

      const reportData = response?.data?.data || response?.data || [];

      if (reportData.length === 0) {
        toast.error("No data returned for report");
        return;
      }

      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text("Stock Allotment History Report", 14, 22);

      const tableColumn = ["Item Name", "Category", "Allotted Qty", "Issued By", "Date & Time", "Purpose"];
      const tableRows = reportData.map((r) => {
        const itemName = r?.item?.name || "N/A";
        const categoryName = r?.category?.name || "N/A";
        const qty = r?.stockAlloted ?? 0;
        const issuedBy = r?.createdBy?.name || "N/A";
        const { date, time } = formatAllotmentDate(r.createdAt);
        const purpose = r?.comment || "No comment";
        return [itemName, categoryName, qty, issuedBy, `${date} ${time}`, purpose];
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 30,
        theme: "striped",
      });

      doc.save(`Campus_Allotment_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to generate report");
    }
  };

  const { data: categoriesData } = useGetCategoriesQuery(1);
  const categories = categoriesData?.data?.categories || [];

  const allotments = data?.data?.data || data?.data || [];
  const total = data?.total || data?.data?.total || 0;
  const totalPages = limit === "All" ? 1 : Math.max(1, Math.ceil(total / limit));

  const renderRow = (r, idx) => {
    const { date, time } = formatAllotmentDate(r.createdAt);
    const itemImage = r?.item?.itemPicture?.secure_url || itmImg;
    const profileImage = r?.createdBy?.profilePicture?.secure_url;

    return (
      <div
        key={r._id || idx}
        className={`bg-[#EEEEEE40] ${idx === allotments.length - 1 ? "pb-48" : ""}`}
      >
        <div className="grid grid-cols-12 gap-4 px-7 py-6 items-center border-b border-[#E5E5E5] ">
          <div className="col-span-2 flex items-center gap-[14px] text-black">
            <div className="w-[53px] h-[42px] overflow-hidden">
              <img
                src={itemImage}
                alt={r?.item?.name || "item"}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-black capitalize">{r?.item?.name || "N/A"}</p>
              <p className="text-[#279B9F] text-[10px] leading-[13px]">
                {r?.category?.name || "N/A"}
              </p>
            </div>
          </div>

          <div className="col-span-2 text-center">
            <div>
              <p className="text-black">{r?.stockAlloted ?? 0} Units</p>
              <p className="text-[10px] text-[#919191] leading-3">
                At {r?.totalStockAtThatTime ?? 0} units
              </p>
            </div>
          </div>

          <div className="col-span-2 flex items-center justify-center gap-1.5">
            <div className="w-[29px] h-[29px] bg-[#DAE8ED] rounded-full flex justify-center items-center overflow-hidden">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt={r?.createdBy?.name || "user"}
                  className="h-full w-full object-cover"
                />
              ) : (
                <ProfileIcon />
              )}
            </div>
            <div>
              <p className="text-black leading-5">
                {r?.createdBy?.name || "N/A"}
              </p>
              <p className="text-[10px] text-[#919191] leading-3">
                Campus Staff
              </p>
            </div>
          </div>

          <div className="col-span-3 text-center">
            <p className="text-xs leading-5 text-[#9D9C9C]">
              {r?.comment || "No comment provided"}
            </p>
          </div>

          <div className="col-span-3 flex flex-col items-center">
            <p className="text-black leading-[17px]">{date}</p>
            <p className="text-[#929292] text-sm leading-[17px]">{time}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="pt-14 pl-8 pr-12 min-h-screen">
      <div className="flex justify-between items-start">
        <HeaderInfo
          title="Stock Allotment History"
          desc="Detailed trail of internal item distributions within your branch"
        />
        <button
          onClick={handleGenerateReport}
          disabled={isGenerating}
          className="flex items-center gap-2 bg-[#279B9F] text-white px-4 py-2 rounded-[8px] text-sm font-medium hover:bg-[#1f7a7d] transition-colors disabled:opacity-50"
        >
          <Download size={16} /> {isGenerating ? "Generating..." : "Generate Report"}
        </button>
      </div>

      <div className="rounded-[10px] border border-[#D9D9D9] mt-4 pt-6 ">
        <div className="pl-5 pr-6">
          <div className=" border border-[#D9D9D9] rounded-[10px] py-4 pl-6 pr-4  flex flex-wrap items-center justify-center gap-4">
            <div className="flex items-center flex-1 min-w-[250px] gap-2 py-3 px-4 bg-[#EEEEEE40] rounded-[10px]">
              <Search />
              <input
                type="text"
                placeholder="Filter by Item or Comment..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent outline-none text-black placeholder:text-[#878686]"
              />
            </div>
            <div className="flex flex-wrap justify-center gap-3 w-full md:w-auto">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm min-w-[160px] capitalize"
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c._id} value={c._id} className="capitalize">{c.name}</option>
                ))}
              </select>

              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm"
                />
                <span className="text-[#878686]">to</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm"
                />
              </div>

              <select
                value={limit}
                onChange={(e) => setLimit(e.target.value === "All" ? "All" : Number(e.target.value))}
                className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm"
              >
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value="All">Show All</option>
              </select>
              
              <button
                onClick={() => {
                  setSearch("");
                  setDebouncedSearch("");
                  setSelectedCategory("");
                  setFromDate("");
                  setToDate("");
                  setLimit(10);
                  setPage(1);
                }}
                className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>
        <div className="mt-4">
          {isLoading || isFetching ? (
            <p className="px-7 py-6 text-sm text-[#6B7280]">
              Loading allotments...
            </p>
          ) : allotments.length === 0 ? (
            <p className="px-7 py-6 text-sm text-[#6B7280]">
              No allotments found.
            </p>
          ) : (
            <DataTable
              columns={[
                { key: "item", label: "Item", className: "col-span-2" },
                {
                  key: "allotted-qty",
                  label: "Allotted Qty",
                  className: "col-span-2 text-center",
                },
                {
                  key: "issued",
                  label: "Issued By",
                  className: "col-span-2 text-center",
                },
                {
                  key: "purpose",
                  label: "Purpose / Comment",
                  className: "col-span-3 text-center",
                },
                {
                  key: "date&time",
                  label: "Date & Time",
                  className: "col-span-3 text-center",
                },
              ]}
              rows={allotments}
              renderRow={renderRow}
              page={page}
              totalPages={totalPages}
              onNext={() => setPage((p) => Math.min(p + 1, totalPages))}
              onPrev={() => setPage((p) => Math.max(p - 1, 1))}
            />
          )}
        </div>
      </div>
    </div>
  );
}
